import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux'; // 1. Redux Imports
import { List } from 'antd';

import FieldUserBrief from './FieldUserBrief';
import SelectedFieldUser from './SelectedFieldUser';
import { useSubscribeResources } from '../DateTimePicker/effects';
import { subscribe } from '../../HOC/Firebase/SubscribeHOC';
import { auth } from '../../Auth';
import { TYPES } from '../reducer'; // 2. Import Action Types

export function FieldUsers({ makeSubscription, subscribeResources, modalType }) {
  const dispatch = useDispatch();

  // 3. Selectors: Only grab the specific data this component needs
  // We default to {} to prevent crashes if mapping is undefined
  const filteredInspectors = useSelector(state => state.mapping?.filteredInspectors || {});
  const inspector = useSelector(state => state.inspector || {});
  const allInspectors = useSelector(state => state.inspectors || {});

  // 4. Replacement for 'pushData' utility
  // Since 'pushData' in your reducer just spreads the value into state,
  // we can create a simple helper here.
  const push = (payload) => {
    dispatch({ type: TYPES.PUSH_DATA, payload });
  };

  const subscriptionOptions = useMemo(() => ({
    paths: [{ collection: `users` }],
    options: {
      where: [
        { field: `type`, test: `==`, value: `field-user` },
        { field: 'organization.id', test: '==', value: auth.sunkaizenUser.organization.id },
        { field: 'geocode_results', test: '!=', value: null }
      ],
      order: `name`,
    },
  }), [auth.sunkaizenUser.organization.id]);

  useEffect(function () {
    auth.sunkaizenUserSubscribe((user) => {
      if (!user) return;
      if (user.type === `field-user`) {
        dispatch({ type: TYPES.INSPECTORS, payload: { [user.id]: user } });
        return;
      }

      // GEMINI SUGGESTION: Pass the memoized options here
      return makeSubscription(
        subscriptionOptions,
        `inspectors` 
      );
    });
  }, [subscriptionOptions]);

  useSubscribeResources(subscribeResources);

  const fieldUsers = useMemo(() => {
    // Safety check: filteredInspectors might be empty initially
    let users = Object.values(filteredInspectors);
    
    if (auth.sunkaizenUser?.type === 'client') { 
      users.push(auth.sunkaizenUser);
    }

    return users
      .filter((user) => user.id !== inspector?.id && !user.disabled)
      .sort((a, b) => {
        const nameA = String(a.name || "").toLowerCase();
        const nameB = String(b.name || "").toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
  }, [filteredInspectors, inspector?.id, auth.sunkaizenUser.type, auth.sunkaizenUser.id]); 

  const totalCount = Object.keys(allInspectors).length;
  const visibleCount = fieldUsers.length;
  const isLoading = totalCount === 0;
  console.log('FieldUsers renders');

  return (
    <div className="padding-top-bottom-2">
      <div className="flex-row justify-space-between align-center">
        <div className="bold padding-0-5">Inspector {totalCount > 0 && `(${visibleCount}/${totalCount})`}</div>
        {inspector.id && (
          <a 
            className="padding-right-1 justify-end" 
            style={{ fontSize: '1.25rem' }} 
            href="#" 
            onClick={(e) => {
              e.preventDefault(); // Good practice for anchor tags
              push({ inspector: {} });
            }}
          >
            Clear Selected Inspector
          </a>
        )}
      </div>

      {/* NOTE: Ensure SelectedFieldUser is also converted if it uses Context! */}
      {inspector.id && <SelectedFieldUser inspector={inspector} />}
      
      <div className="bold padding-top-1">Field Users</div>
      <small>Please select an inspector</small>
      
      <List
        dataSource={fieldUsers}
        loading={isLoading}
        locale={{ emptyText: "No inspector found in the visible area" }}
        renderItem={(user) => <FieldUserBrief inspector={user} />}
        style={{ maxHeight: modalType ? '10em' : '30em', overflow: 'scroll' }}
      />
    </div>
  );
}

// Keep the HOC wrapper
const WrappedFieldUsers = subscribe()(FieldUsers);
export default WrappedFieldUsers;